import { create } from 'zustand';
import { supabase, getCurrentUser } from '../lib/supabase';

interface User {
  id: string;
  email?: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  checkAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ error: any }>;
  signup: (email: string, password: string) => Promise<{ error: any }>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  
  checkAuth: async () => {
    try {
      set({ isLoading: true });
      const user = await getCurrentUser();
      set({ 
        user: user || null, 
        isAuthenticated: !!user,
        isLoading: false 
      });
    } catch (error) {
      console.error('Error checking auth:', error);
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
  
  login: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (data.user) {
        set({ 
          user: data.user, 
          isAuthenticated: true 
        });
      }
      
      return { error };
    } catch (error) {
      console.error('Login error:', error);
      return { error };
    }
  },
  
  signup: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) {
        return { error };
      }
      
      if (data.user) {
        // Set authenticated state but don't redirect yet
        // User may need to confirm email depending on settings
        set({ 
          user: data.user,
          isAuthenticated: true 
        });
      }
      
      return { error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { error };
    }
  },
  
  logout: async () => {
    await supabase.auth.signOut();
    set({ user: null, isAuthenticated: false });
  },
}));

// Initialize auth state check when store is imported
useAuthStore.getState().checkAuth();